import { memo } from 'react';
import { Box } from '@mui/material';
import { ChartData, ChartOptions, Point } from 'chart.js';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import { Line } from 'react-chartjs-2';

import { KpiDetailsProps } from './kpi-details';
import { formatValueForChart } from './value-formater';

const KpiChartSiteHistory: React.FC<KpiDetailsProps> = memo(({ kpi, project, historyItems }) => {
  const historyData = historyItems?.get(kpi.id)?.projectValues;
  const orderedData = orderBy(
    Object.values(historyData ?? {}),
    (d) => new Date(d.applicableYear, d.applicableMonth - 1)
  );
  const mappedData = orderedData.map(
    (d) =>
      ({
        x: dayjs(new Date(d.applicableYear, d.applicableMonth - 1)).format('YYYY-MM-DD'),
        y: d.value,
        backgroundColor: '#dd0',
      }) as unknown as Point
  );

  if (mappedData.length === 0) {
    return (
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        No data to display
      </Box>
    );
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'MM.YYYY',
          },
          tooltipFormat: 'MM.YYYY',
        },
        ticks: {
          autoSkipPadding: 10,
          maxRotation: 0,
        },
        offset: true,
      },
      y: {
        beginAtZero: true,
      },
    },
    layout: {
      padding: {
        top: 30,
        right: 20,
      },
    },
    interaction: {
      axis: 'x',
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        formatter: (value) => formatValueForChart(value?.y, kpi, 4),
        color: '#212636',
        anchor: 'end',
        align: 'top',
        clip: false,
      },
      tooltip: {
        boxPadding: 5,
        callbacks: {
          label: (item) => formatValueForChart(item.parsed.y, kpi, 4),
          title: (items) => {
            const item = items?.at(0);
            const time = parseInt(`${item?.parsed.x}`);

            if (item && time > 0) {
              const date = dayjs(new Date(time)).format('MM.YYYY');
              const suffix = project.id === orderedData[item?.dataIndex]?.projectId ? ' (current)' : '';
              return `${date}${suffix}`;
            }

            return '-';
          },
        },
      },
    },
  };

  const colors = orderedData.map((x) => (x.projectId === project.id ? '#2e96ff' : '#02b2af'));
  const pointSizes = orderedData.map((x) => (x.projectId === project.id ? 6 : 4));

  const chartData: ChartData<'line'> = {
    datasets: [
      {
        data: mappedData,
        backgroundColor: '#02b2af',
        borderColor: '#02b2af',
        tension: 0,
        pointBackgroundColor: colors,
        pointRadius: pointSizes,
        pointHoverRadius: pointSizes,
      },
    ],
  };

  return <Line options={options} data={chartData} />;
});

export default KpiChartSiteHistory;
